import { Detection } from '../generated/graphql';
import { SantizedDetection } from '@investigator/features/detections/pages/Detections';
import { AlertEntity, EntityCategory } from '../generated/graphql';

function normalizeEntityCategory(
  entity: AlertEntity | { entity_category: string } | undefined,
): AlertEntity | undefined {
  if (!entity) return undefined;
  let normalizedCategory: EntityCategory | undefined = undefined;
  if (
    entity.entity_category === 'source' ||
    entity.entity_category === EntityCategory.Source
  )
    normalizedCategory = EntityCategory.Source;
  if (
    entity.entity_category === 'destination' ||
    entity.entity_category === EntityCategory.Destination
  )
    normalizedCategory = EntityCategory.Destination;
  return {
    ...entity,
    entity_category: normalizedCategory,
  } as AlertEntity;
}

/**
 * Smart source entity selection for suppression actions.
 *
 * This function determines which source entity should be used for suppression
 * when multiple source entities exist in a detection.
 *
 * Logic:
 * 1. If alert_entity is a source entity, try to find it in paginated_source_entities
 * 2. If found, use the matching entity from paginated list (has more complete data)
 * 3. If not found, use alert_entity directly (it's still a valid source)
 * 4. If alert_entity is not a source, use first source entity from paginated list
 * 5. Return undefined if no valid source entity found
 *
 * @param detection - The detection object containing entity information
 * @returns The best source entity for suppression, or undefined if none found
 */
export const getSourceEntityForSuppression = (
  detection?: Detection | SantizedDetection,
): AlertEntity | undefined => {
  if (!detection) {
    return undefined;
  }

  const alertEntityCategory = detection.alert_entity?.entity_category as
    | EntityCategory
    | string
    | null
    | undefined;
  const isSourceCategory =
    alertEntityCategory === 'source' ||
    alertEntityCategory === EntityCategory.Source;

  // If alert_entity is a source entity, try to find it in paginated_source_entities
  if (isSourceCategory) {
    const alertEntity = detection.alert_entity;

    // Try to find matching entity in paginated source entities
    const matchingSourceEntity =
      detection.paginated_source_entities?.alert_entities?.find(
        (entity) =>
          entity != null &&
          (entity.entity_id === alertEntity.entity_id ||
            entity.entity_name === alertEntity.entity_name),
      );

    if (matchingSourceEntity) {
      return normalizeEntityCategory(matchingSourceEntity);
    }

    // If no match found in paginated entities, use alert_entity directly
    return normalizeEntityCategory(alertEntity);
  }

  // If alert_entity is not a source, try first source entity from paginated list
  const firstSourceEntity =
    detection.paginated_source_entities?.alert_entities?.[0];
  if (firstSourceEntity) {
    return normalizeEntityCategory(firstSourceEntity);
  }

  // Return undefined if no valid source entity found
  return undefined;
};
